import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import LoaderComponent from 'components/Loader';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import ReactECharts from 'echarts-for-react';
import CustomDropdown from 'components/Dropdown';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from '@mui/material/styles';
import { InfoTooltip } from 'components/Widgets/InfoTooltip';

type KpiWithRadialGaugeProps = {
	isLoading?: boolean;
	title: string | React.ReactNode;
	number: number | string;
	numberSuffix?: string;
	change?: number;
	changeType?: 'increase' | 'decrease';
	showChangePercentage?: boolean;
	gaugeValue: number;
	utilizedCost: number | string;
	gaugeColor?: string;
	backgroundColor?: string;
	color?: string;
	containerProps?: any;
	hasDropdown?: boolean;
	hasTooltip?: boolean;
	tooltipText?: string;
	dropdownValue?: string;
	dropdownOptions?: { label: string; value: string }[];
	onDropdownChange?: (value: string) => void;
};

const KpiWithRadialGauge = ({
	isLoading = false,
	title,
	number,
	numberSuffix,
	change,
	changeType = 'increase',
	showChangePercentage = false,
	gaugeValue = 0,
	utilizedCost = 0,
	color,
	containerProps,
	hasDropdown = false,
	hasTooltip = false,
	tooltipText,
	dropdownValue,
	dropdownOptions = [],
	onDropdownChange
}: KpiWithRadialGaugeProps) => {
	const theme = useTheme();
	const colors = theme.palette;

	const resolvedColor = color || colors.widget?.text?.main;
	const safeGaugeValue = Math.min(Math.max(gaugeValue, 0), 100);

	const getChangeColor = () => {
		if (change === undefined || change === 0) return colors.widget?.text?.noChange;
		return changeType === 'increase'
			? colors.widget?.text?.changeUp
			: colors.widget?.text?.changeDown;
	};

	const renderChangeIcon = () => {
		if (change === undefined || change === 0) return null;

		return changeType === 'increase'
			? <ArrowUpward fontSize="small" sx={{ fontSize: 20 }} />
			: <ArrowDownward fontSize="small" sx={{ fontSize: 20 }} />;
	};

	const renderKpiSection = () => {
		return (
			<Stack
				color={resolvedColor}
				direction="column"
				alignItems="start"
				width="100%"
			>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					width="100%"
				>
					<Stack direction="row" alignItems="center" gap={1}>
						<Typography
							variant="widgetTitle"
							sx={{
								color: colors.widget?.text?.title
							}}
						>
							{typeof title === 'string'
								? <HighlightedText text={title} />
								: title}
						</Typography>

						{hasTooltip && tooltipText && (
							<InfoTooltip label={tooltipText} />
						)}
					</Stack>

					{hasDropdown && dropdownValue && dropdownOptions.length > 0 && (
						<CustomDropdown
							value={dropdownValue}
							options={dropdownOptions}
							handleChange={(event: any) => {
								if (onDropdownChange) {
									onDropdownChange(event.target.value);
								}
							}}
							dropdownIcon={KeyboardArrowDownIcon}
							selectedTextColor={colors.DropdownV2?.dropdownIconDefault}
							textColor={colors.DropdownV2?.menuItemText}
							iconColor={colors.DropdownV2?.dropdownIconDefault}
							bgColor={colors.DropdownV2?.menuItemBackground}
							borderColor="transparent"
							hoverBgColor={colors.DropdownV2?.menuItemHover}
							hoverTextColor={colors.DropdownV2?.menuItemHoverText}
							selectedBgColor={colors.DropdownV2?.menuItemSelectedBg}
							boxProps={{
								sx: {
									backgroundColor: 'transparent !important',
									minWidth: '70px',
									'& .MuiOutlinedInput-root': {
										backgroundColor: 'transparent !important'
									},
									'& .MuiSelect-select': {
										fontFamily: 'Montserrat',
										fontSize: '14px',
										fontWeight: 500,
										padding: '0 24px 0 8px !important'
									},
									'& .MuiOutlinedInput-notchedOutline': {
										border: 'none !important'
									},
									'& .MuiSelect-icon': {
										color: `${colors.DropdownV2?.dropdownIconDefault} !important`,
										right: 4
									}
								}
							}}
						/>
					)}
				</Stack>

				{!isLoading && (
					<>
						<Typography
							variant="metricValue"
							sx={{
								color: resolvedColor,
								mt: 1,
								mb: showChangePercentage ? 1 : 0,
								display: 'flex',
								alignItems: 'baseline',
								gap: 1
							}}
						>
							<Box component="span">$</Box>

							<Box component="span">
								{typeof number === 'number'
									? number.toFixed(2)
									: number}
							</Box>

							{numberSuffix && (
								<Box
									component="span"
									sx={{
										fontWeight: 400,
										lineHeight: 1
									}}
								>
									{numberSuffix}
								</Box>
							)}
						</Typography>

						<Box
							display={showChangePercentage ? 'flex' : 'none'}
							alignItems="center"
							color={getChangeColor()}
						>
							{renderChangeIcon()}

							<Typography
								variant="bodyText"
								ml={0.5}
								sx={{
									color: getChangeColor(),
									fontSize: 20
								}}
							>
								{change === 0 ? '0%' : `${change}%`} vs last month
							</Typography>
						</Box>
					</>
				)}
			</Stack>
		);
	};

	const radialGaugeOption: any = {
		backgroundColor: 'transparent',
		series: [
			{
				type: 'gauge',
				center: ['50%', '55%'],
				radius: '80%',
				min: 0,
				max: 100,
				emphasis: {
					disabled: true
				},
				pointer: {
					show: false
				},
				progress: {
					show: false
				},
				axisLine: {
					show: true,
					lineStyle: {
						width: 20,
						color: [
							[
								1,
								colors.widget?.gaugeChart?.unfilledArcColor
							]
						]
					}
				},
				axisTick: {
					show: false
				},
				splitLine: {
					show: false
				},
				axisLabel: {
					show: false
				}
			},
			{
				type: 'gauge',
				center: ['50%', '55%'],
				radius: '80%',
				min: 0,
				max: 100,
				emphasis: {
					disabled: true
				},
				pointer: {
					show: false
				},
				axisLine: {
					show: false
				},
				progress: {
					show: true,
					width: 20,
					roundCap: false,
					itemStyle: {
						color: colors.widget?.gaugeChart?.filledArcColor
					}
				},
				axisTick: {
					show: false
				},
				splitLine: {
					show: false
				},
				axisLabel: {
					show: false
				},
				detail: {
					valueAnimation: true,
					offsetCenter: [0, 12],
					fontSize: 18,
					fontWeight: '700',
					formatter: () => {
						const formattedCost =
							typeof utilizedCost === 'number'
								? utilizedCost.toFixed(2)
								: utilizedCost;

						return formattedCost;
					},
					color: colors.widget?.text?.value
				},
				data: [
					{
						value: safeGaugeValue
					}
				]
			},
			{
				type: 'gauge',
				center: ['50%', '55%'],
				radius: '80%',
				min: 0,
				max: 100,
				emphasis: {
					disabled: true
				},
				pointer: {
					show: false
				},
				axisLine: {
					show: false
				},
				progress: {
					show: false
				},
				axisTick: {
					show: false
				},
				splitLine: {
					show: false
				},
				axisLabel: {
					show: false
				},
				detail: {
					offsetCenter: [0, '-12%'],
					fontSize: 11,
					fontWeight: '400',
					formatter: `Total Utilized Cost (${safeGaugeValue.toFixed(2)}%)`,
					color: colors.widget?.chart?.labelColorSecondary
				},
				data: [
					{
						value: safeGaugeValue
					}
				]
			}
		]
	};

	const renderRadialGauge = () => {
		return (
			<ReactECharts
				option={radialGaugeOption}
				style={{
					width: '100%',
					height: '100%'
				}}
				opts={{
					renderer: 'canvas',
					width: 'auto',
					height: 'auto'
				}}
				notMerge={true}
				lazyUpdate={true}
			/>
		);
	};

	return (
		<Box
			display="flex"
			flexGrow={1}
			height="100%"
			position="relative"
			{...containerProps}
		>
			<Stack
				p={{ xs: 2, sm: 2 }}
				bgcolor={colors.widget?.background?.card}
				fontFamily="Montserrat"
				borderRadius="5px"
				width="100%"
				flexGrow={1}
				sx={{
					flexDirection: 'column',
					justifyContent: 'flex-start',
					overflow: 'hidden',
					...containerProps?.sx
				}}
			>
				<Stack
					justifyContent="flex-start"
					width="100%"
				>
					{renderKpiSection()}
				</Stack>

				<Stack
					flexGrow={1}
					minHeight={0}
					width="100%"
					alignItems="center"
					justifyContent="center"
				>
					{isLoading ? (
						<Stack
							flexGrow={1}
							justifyContent="center"
							alignItems="center"
							width="100%"
						>
							<LoaderComponent />
						</Stack>
					) : (
						<Box
							height="260px"
							width="100%"
							maxWidth="420px"
							display="flex"
							alignItems="center"
							justifyContent="center"
						>
							{renderRadialGauge()}
						</Box>
					)}
				</Stack>
			</Stack>
		</Box>
	);
};

export default KpiWithRadialGauge;